import React, { useEffect, useRef, useState } from 'react';
import { imageTone } from './tone.js';
import { softImage } from './soft-image.js';
import { SoftLayer } from './SoftLayer.js';
import { canvasVeil } from './wash-level.js';

/** Что показать за тайтлом: кадр, его крошечная копия и id для сравнения. */
export interface StageSource {
  /** Смена ключа — смена тайтла: по ней и запускается растворение. */
  key: string;
  /** Чёткий кадр (backdrop); `null` — кадра нет, остаётся подложка. */
  art: string | null;
  /** Крошечная копия постера (≈w92): из неё подложка и цвет тайтла. */
  tiny: string | null;
  /**
   * Постер — на случай, когда кадра нет: тогда справа стоит он, целиком, на
   * размытой подложке. Пустой сцены не бывает.
   */
  poster?: string | null;
  /**
   * Копия кадра (w780; нет кадра — постера w342) для мягкого фона страницы
   * тайтла: из неё один раз считается слегка размытый кадр (`soft-image.ts`),
   * в котором проступают силуэты и свет.
   */
  wash?: string | null;
}

interface Slot {
  source: StageSource;
  ambient: string | null;
  /**
   * Готовая размытая подложка (`soft-image.ts`); `null` — холста нет, и
   * подложкой остаётся сама маленькая картинка.
   */
  backdrop: HTMLCanvasElement | null;
  /**
   * Доля мягкого фона под текстом — по яркости самого кадра (`wash-level.ts`):
   * светлый кадр притушен сильнее, чтобы текст на нём читался. `null` — у
   * чёткой сцены и там, где кадр не прочитать: остаётся доля из стилей.
   */
  veil: number | null;
}

/** Сколько ждём картинку, прежде чем растворять без неё: фон не должен зависать. */
const DECODE_BUDGET_MS = 1200;

/** Готовая подложка, если успела к сроку; не успела — сцена выходит без неё. */
function withinBudget(pending: Promise<HTMLCanvasElement | null>): Promise<HTMLCanvasElement | null> {
  const budget = new Promise<null>((resolve) => window.setTimeout(() => resolve(null), DECODE_BUDGET_MS));
  return Promise.race([pending, budget]);
}

function decoded(url: string | null): Promise<void> {
  if (url === null) return Promise.resolve();
  const image = new Image();
  image.decoding = 'async';
  image.src = url;
  const budget = new Promise<void>((resolve) => window.setTimeout(resolve, DECODE_BUDGET_MS));
  return Promise.race([image.decode().catch(() => undefined), budget]);
}

/**
 * Сколько чёткая сцена ждёт свой кадр. Срок большой нарочно: пока кадр нового
 * тайтла не разобран, на экране остаётся чёткий кадр предыдущего. С коротким
 * сроком (как у подложки) слот выходил на сцену без кадра — одна размытая
 * подложка во весь верх, а кадр доезжал позже: при наведении на карточки это
 * читалось как «страница размывается» (владелец, 05.10.2026). По истечении
 * срока кадр считается недоступным.
 */
const ART_WAIT_MS = 20_000;

/** Картинка скачана и разобрана? `false` — её нет, она не загрузилась или не успела к сроку. */
function ready(url: string | null | undefined): Promise<boolean> {
  if (url == null) return Promise.resolve(false);
  const image = new Image();
  image.decoding = 'async';
  image.src = url;
  const late = new Promise<boolean>((resolve) => window.setTimeout(() => resolve(false), ART_WAIT_MS));
  return Promise.race([image.decode().then(() => true, () => false), late]);
}

/**
 * Что чёткая сцена покажет на самом деле: тайтл с готовым кадром; кадр не
 * загрузился — тот же тайтл без кадра (тогда справа встаёт постер на подложке,
 * как у тайтла, у которого кадра нет вовсе). Постер к выходу тоже разобран.
 */
async function sharp(source: StageSource): Promise<StageSource> {
  if (await ready(source.art)) return source;
  const bare = source.art === null ? source : { ...source, art: null };
  await ready(bare.poster);
  return bare;
}

/**
 * Картинки сцены — заранее, для тайтла, который вот-вот встанет на неё сам
 * (автопоказ главной): кадр уже скачан и разобран, цвет постера посчитан, и
 * смена тайтла начинается сразу, а не после загрузки.
 */
export function preloadStage(source: StageSource): void {
  void decoded(source.art ?? source.poster ?? null);
  if (source.tiny !== null) {
    void imageTone(source.tiny).catch(() => null);
    void softImage(source.tiny);
  }
}

/**
 * Длительность растворения слотов — та же, что `--dur-dissolve` в стилях.
 * Нужна здесь, чтобы не начинать следующую смену раньше конца идущей.
 */
const STAGE_FADE_MS = 700;

/**
 * Запас сверх растворения. Отсчёт здесь начинается до того, как браузер
 * нарисует кадр и запустит анимацию слота, — она кончается на кадр-другой
 * позже. А слот, у которого анимация ещё идёт, при потере роли верхнего не
 * держит свою непрозрачность (браузер не запускает переход поверх идущей
 * анимации) и гаснет разом: вместо растворения — провал в темноту. Найдено
 * на замере: две смены подряд через 700 мс давали яркость 124 → 10 → 114.
 */
const STAGE_FADE_GUARD_MS = 120;

/** Просили меньше движения — слои меняются сразу, ждать нечего. */
function calm(): boolean {
  return typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Сцена за тайтлом — фон главной и страницы тайтла.
 *
 * Три слоя на тайтл: размытая подложка из маленькой копии постера или кадра
 * (размытие считается один раз на маленьком холсте, `soft-image.ts`, а браузер
 * лишь растягивает готовую гладкую картинку), мягкий отсвет основным цветом
 * постера и чёткий кадр справа, растворённый маской к тексту. Ни
 * `filter: blur`, ни `backdrop-filter`: на слабых машинах они пересчитываются
 * на каждом кадре движения.
 *
 * Смена тайтла — растворением между двумя слотами: новый слот проявляется
 * прозрачностью поверх старого, а меняется картинка только у невидимого. Новый
 * кадр сперва декодируется и лишь потом выходит на сцену — иначе растворение шло
 * бы в пустоту, а картинка доезжала рывком.
 */
export function TitleStage({
  source,
  className,
  soft = false,
  onShown,
}: {
  /** `null` — сцена стоит как есть: ни загрузок, ни смены. */
  source: StageSource | null;
  className?: string;
  /**
   * Только размытая подложка и отсвет, без чёткого кадра: фон страницы
   * тайтла не должен спорить с постером и текстом — картинка на нём
   * угадывается, но не отвлекает.
   */
  soft?: boolean;
  /** Тайтл встал на сцену: его картинки готовы, растворение началось. */
  onShown?: (key: string) => void;
}): React.JSX.Element {
  const [slots, setSlots] = useState<[Slot | null, Slot | null]>([null, null]);
  const [front, setFront] = useState<0 | 1>(0);
  const frontRef = useRef<0 | 1>(0);
  const shownKey = useRef<string | null>(null);
  const token = useRef(0);
  // Когда закончится идущее растворение: следующая смена начинается не раньше.
  const settledAt = useRef(0);
  const onShownRef = useRef(onShown);
  onShownRef.current = onShown;
  // Сцену убрали, пока картинка ехала, — ответ уже никому не нужен.
  useEffect(() => () => {
    token.current += 1;
  }, []);

  useEffect(() => {
    if (source === null || source.key === shownKey.current) return;
    const ticket = ++token.current;
    // Подложка: на странице тайтла — из кадра (нет его — из постера), на
    // главной — из постера, под чётким кадром.
    const underlay = soft ? (source.wash ?? source.tiny) : source.tiny;
    void Promise.all([
      soft ? Promise.resolve(source) : sharp(source),
      source.tiny !== null ? imageTone(source.tiny) : Promise.resolve(null),
      // Фон страницы тайтла — лёгкое размытие крупного кадра: образы
      // проступают. Подложка главной — глубокое, из крошечной копии.
      underlay != null
        ? withinBudget(softImage(underlay, soft && source.wash != null ? 'light' : 'deep'))
        : Promise.resolve(null),
    ]).then(async ([shown, tone, backdrop]) => {
      // Пока грузилось, фокус ушёл дальше — этот тайтл уже не нужен.
      if (ticket !== token.current) return;
      // Слотов два: если начать новую смену, пока идёт прежняя, нижний слой
      // подменится на полпути — кадр дёрнется вместо растворения. Поэтому
      // дожидаемся конца идущего растворения; за это время победит самый
      // свежий тайтл (остальные отсеет `ticket`).
      const wait = settledAt.current - Date.now();
      if (wait > 0 && !calm()) {
        await new Promise<void>((done) => { window.setTimeout(done, wait); });
        if (ticket !== token.current) return;
      }
      settledAt.current = Date.now() + STAGE_FADE_MS + STAGE_FADE_GUARD_MS;
      shownKey.current = source.key;
      const next: 0 | 1 = frontRef.current === 0 ? 1 : 0;
      frontRef.current = next;
      setSlots((prev) => {
        const copy: [Slot | null, Slot | null] = [prev[0], prev[1]];
        copy[next] = {
          source: shown,
          ambient: tone?.ambient ?? null,
          backdrop,
          veil: soft && source.wash != null ? canvasVeil(backdrop) : null,
        };
        return copy;
      });
      setFront(next);
      onShownRef.current?.(source.key);
    });
  }, [source, soft]);

  return (
    <div className={['stage', soft ? 'is-soft' : '', className ?? ''].filter(Boolean).join(' ')} aria-hidden="true">
      {slots.map((slot, index) =>
        slot === null ? null : (
          <div
            key={index}
            className={index === front ? 'stage-slot is-front' : 'stage-slot'}
            style={slotStyle(slot)}
          >
            <StageUnderlay slot={slot} soft={soft} />
            <div className="stage-ambient" />
            {soft ? null : slot.source.art !== null ? (
              <img className="stage-art" src={slot.source.art} alt="" decoding="async" />
            ) : (
              slot.source.poster != null && (
                <img className="stage-poster" src={slot.source.poster} alt="" decoding="async" />
              )
            )}
          </div>
        ),
      )}
      <div className="stage-scrim" />
    </div>
  );
}

/** Переменные слота: цвет отсвета и доля мягкого фона — только те, что известны. */
function slotStyle(slot: Slot): React.CSSProperties | undefined {
  if (slot.ambient === null && slot.veil === null) return undefined;
  const style: Record<string, string> = {};
  if (slot.ambient !== null) style['--slot-ambient'] = slot.ambient;
  if (slot.veil !== null) style['--wash-veil'] = slot.veil.toFixed(3);
  return style as React.CSSProperties;
}

/**
 * Подложка слота: готовый размытый холст, а где холста нет — та же маленькая
 * картинка, растянутая браузером (как было раньше).
 */
function StageUnderlay({ slot, soft }: { slot: Slot; soft: boolean }): React.JSX.Element | null {
  const className = soft ? 'stage-atmos stage-wash' : 'stage-atmos';
  if (slot.backdrop !== null) return <SoftLayer image={slot.backdrop} className={className} />;
  const fallback = soft ? (slot.source.wash ?? slot.source.tiny) : slot.source.tiny;
  if (fallback == null) return null;
  return <div className={className} style={{ backgroundImage: `url("${fallback}")` }} />;
}
